type AuthMessage =
    | { type: 'TOKEN_UPDATE'; token: string; tabId: string }
    | { type: 'TOKEN_REQUEST'; tabId: string }
    | { type: 'LOGOUT'; token?: string; tabId: string; identity?: { _id?: string; _profileId?: string; _classId?: string } }

const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

class AuthBroadcastService {
    private channel: BroadcastChannel | null = null
    private listeners: Set<(message: AuthMessage) => void> = new Set()
    private storageListener: ((e: StorageEvent) => void) | null = null

    constructor() {
        if (typeof window !== 'undefined') {
            // Attach storage listener always as a fallback; channel will be created lazily
            this.storageListener = this.handleStorageEvent.bind(this)
            window.addEventListener('storage', this.storageListener)
            this.ensureChannel()
        }
    }

    private ensureChannel(): void {
        if (this.channel) return
        if (typeof window === 'undefined') return
        try {
            if ('BroadcastChannel' in window) {
                this.channel = new BroadcastChannel('egg-admin-auth')
                this.channel.onmessage = this.handleMessage.bind(this)
            }
        } catch (e) {
            // ignore environments that don't support BroadcastChannel or throw on construction
            this.channel = null
        }
    }

    private handleMessage(event: MessageEvent<AuthMessage>) {
        const message = event.data
        if (message.tabId === TAB_ID) return
        this.listeners.forEach((listener) => listener(message))
    }

    private handleStorageEvent(e: StorageEvent) {
        try {
            if (!e.key || !e.newValue) return
            // accept keys created by this service fallback, prefixed with 'egg-admin-auth-'
            if (!e.key.startsWith('egg-admin-auth-')) return
            const message: AuthMessage = JSON.parse(e.newValue)
            if (message.tabId === TAB_ID) return
            this.listeners.forEach((listener) => listener(message))
        } catch (error) {
            // ignore malformed messages
        }
    }

    subscribe(callback: (message: AuthMessage) => void): () => void {
        this.listeners.add(callback)
        return () => this.listeners.delete(callback)
    }

    broadcastToken(token: string): void {
        this.ensureChannel()
        const msg = { type: 'TOKEN_UPDATE', token, tabId: TAB_ID }
        if (this.channel) {
            try {
                this.channel.postMessage(msg)
            } catch (e) {
                // ignore post errors and fall back to storage
            }
        }
        // Intentionally do not use localStorage fallback for tokens to avoid persisting JWTs.
    }

    requestToken(): void {
        this.ensureChannel()
        const msg = { type: 'TOKEN_REQUEST', tabId: TAB_ID }
        if (this.channel) {
            try {
                this.channel.postMessage(msg)
            } catch (e) {
                // ignore and fall back to storage
            }
        }

        if (typeof window !== 'undefined') {
            try {
                const key = `egg-admin-auth-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
                // For storage fallback avoid writing sensitive full token for requestToken
                const safeMsg = { type: msg.type, tabId: msg.tabId }
                window.localStorage.setItem(key, JSON.stringify(safeMsg))
                window.localStorage.removeItem(key)
            } catch (error) {
                // ignore
            }
        }
    }

    broadcastLogout(token: string): void {
        // Try to include a small stable identity payload to allow selective logout
        let identity: { _id?: string; _profileId?: string; _classId?: string } | undefined
        try {
            // lazy require to avoid importing jwt lib at module init in some test envs
            // support both default function export and named jwtDecode export (test mocks)
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const _jwt = require('jwt-decode')
            const jwtDecode = typeof _jwt === 'function' ? _jwt : _jwt.jwtDecode || _jwt.default || _jwt
            if (token) {
                try {
                    const payload = jwtDecode(token)
                    identity = {
                        // these fields may or may not exist depending on token shape
                        _id: payload?._id || payload?.sub || undefined,
                        _profileId: payload?._profileId || undefined,
                        _classId: payload?._classId || undefined,
                    }
                } catch (e) {
                    // ignore decode errors
                }
            }
        } catch (e) {
            // jwt-decode not available — ignore
        }

        this.ensureChannel()
        const msg = { type: 'LOGOUT', token, tabId: TAB_ID, identity }
        if (this.channel) {
            try {
                this.channel.postMessage(msg)
            } catch (e) {
                // ignore and fall back to storage
            }
        }

        if (typeof window !== 'undefined') {
            try {
                const key = `egg-admin-auth-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
                // For storage fallback, avoid writing the full token; only broadcast non-sensitive identity data
                const safeMsg = { type: msg.type, tabId: msg.tabId, identity: msg.identity }
                window.localStorage.setItem(key, JSON.stringify(safeMsg))
                window.localStorage.removeItem(key)
            } catch (error) {
                // ignore
            }
        }
    }

    shouldLogout(logoutMessageTokenOrIdentity: string | undefined | { _id?: string; _profileId?: string; _classId?: string }, currentToken: string | null): boolean {
        // If identity object provided, compare against currentToken's decoded identity
        if (!currentToken) return false

        const tryDecode = (t: string) => {
            try {
                // support both default and named exports; tests may mock shape
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                const _jwt = require('jwt-decode')
                const jwtDecode = typeof _jwt === 'function' ? _jwt : _jwt.jwtDecode || _jwt.default || _jwt
                return jwtDecode(t)
            } catch (e) {
                return null
            }
        }

        if (logoutMessageTokenOrIdentity && typeof logoutMessageTokenOrIdentity === 'object') {
            const identity = logoutMessageTokenOrIdentity
            const rawDecoded = tryDecode(currentToken)
            if (!rawDecoded) return false
            // normalize decoded payload to align with broadcastLogout, which uses payload._id || payload.sub
            const decoded: any = { ...rawDecoded }
            if (!decoded._id && decoded.sub) decoded._id = decoded.sub
            // prefer unique identifiers first
            if (identity._id && decoded._id && identity._id === decoded._id) return true
            if (identity._profileId && decoded._profileId && identity._profileId === decoded._profileId) return true
            // classId is a weaker signal — use only if other identifiers absent
            if (
                !identity._id &&
                !identity._profileId &&
                identity._classId &&
                decoded._classId &&
                identity._classId === decoded._classId
            )
                return true
            return false
        }

        // fallback: try compare token strings (legacy)
        return logoutMessageTokenOrIdentity === currentToken
    }

    getTabId(): string {
        return TAB_ID
    }

    destroy(): void {
        if (this.channel) {
            try {
                // remove handler then close
                this.channel.onmessage = null
                this.channel.close()
            } catch (e) {
                // ignore
            }
            this.channel = null
        }
        if (this.storageListener) {
            window.removeEventListener('storage', this.storageListener)
            this.storageListener = null
        }
        this.listeners.clear()
    }
}

export const authBroadcast = new AuthBroadcastService()
export default authBroadcast
